<template>
  <div>
    <div class="contacts-terminals-card-subtitle flex flex-space-between flex-align-items-center">
      График работы
      <client-only>
        <span class="contacts-terminals-card-state" :class="{ 'open': isOpen }">{{ state }}</span>
      </client-only>
    </div>
    <div class="contacts-terminals-card-schedule flex flex-space-between mt-15">
      <div
        v-for="(item, index) in schedule" :key="index"
        class="contacts-terminals-card-schedule-item"
        :class="{'active': item.day === currentDay, 'work': item.time !== '-'}"
        :itemprop="item.datetime ? 'openingHours' : ''"
        :datetime="item.datetime"
      >
        <div>{{ item.day }}</div>
        <div>{{ item.time }}</div>
      </div>
    </div>
    <vz-card v-if="withNote" class="mt-10 p-10">
      {{ terminal.note }}
    </vz-card>
    <div class="contacts-terminals-card-subtitle">
      Ограничения по грузу
    </div>
    <vz-card v-if="isPickupPoint" variant="warning" class="p-10 active">
      {{ terminal.description }}
    </vz-card>
    <span v-else>
      {{ terminal.description }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { useDayjs } from '#dayjs'
import {storeToRefs} from 'pinia'
import {Terminal} from '~/types/types'
import {daysOfWeeks} from '~~/common/date'
import {useCityStore} from '~~/stores/city'
import {useTerminalStore} from '~/stores/terminal'
import {asyncComputed, until} from '@vueuse/core';
import {getPickupPoint} from '~/api/order';
import VzCard from '~/components/ui-kit/vz-card.vue';

const dayjs = useDayjs()
const {currentCity} = storeToRefs(useCityStore())
const {terminalList} = storeToRefs(useTerminalStore())

const props = defineProps({
  terminalGuid: {
    type: String,
    default: '',
    required: true,
  },
  isPickupPoint: {
    type: Boolean,
    default: false
  },
  withNote: {
    type: Boolean,
    default: false
  }
})

const isOpen = ref(true)

const nowDayJs = computed(() => {
  const addHoursTimezone = +(terminal.value?.locationData?.timezone || terminal.value?.timezone || 0);
  return dayjs.utc().add(addHoursTimezone, 'hours')
})

const currentDay = computed(() => {
  return daysOfWeeks[nowDayJs.value.day()].short
})

const currentDaySchedule = computed(() => {
  return schedule.value.find(s => s.day === currentDay.value)?.time || '-'
})

const startNextWorkDay = computed(() => {
  for (let i = 1; 7 >= i; i++) {
    const nextDay = dayjs.utc(nowDayJs.value).add(i, 'day');
    const nextDayShort = daysOfWeeks[nextDay.day()].short

    const nextDayTime = schedule.value.find(s => s.day === nextDayShort)?.time || '-'
    if (nextDayTime !== '-') {
      if (nextDayTime === '24 часа') {
        return nextDay.startOf('day');
      }
      // [10, 30]
      const time = nextDayTime.split(' ')[0].split(':')
      return nextDay.set('hour', +time[0])
        .set('minute', +time[1])
        .set('second', 0)
        .set('millisecond', 0)
    }
  }

  return ''
})

const terminal = asyncComputed<Partial<Terminal> & { timezone?: string }>(async () => {
  if (props.isPickupPoint) {
    return await getPickupPoint(props.terminalGuid)
  }
  return terminalList.value.find(t => t.guid === props.terminalGuid) || {}
}, {})

const willOpenIn = computed(() => {
  const diff = dayjs.utc(+startNextWorkDay.value - +nowDayJs.value)
  const diffD = diff.format('D') === '1' ? '' : `${+diff.format('D') - 1} д.`
  const diffHH = diff.format('HH') === '00' ? '' : `${diff.format('HH')} ч.`
  return `Откроется через ${diffD} ${diffHH} ${diff.format('mm')} мин. `
})

const state = computed(() => {
  if (currentDaySchedule.value === '24 часа') {
    isOpen.value = true
    return 'Открыт целый день'
  }

  if (currentDaySchedule.value === '-') {
    isOpen.value = false
    if (startNextWorkDay.value) {
      return willOpenIn.value;
    }
    // return 'Откроется в рабочий день'
    return 'Закрыто'
  }

  let diff
  let diffHH = ''
  const times = currentDaySchedule.value.split(' ')

  const timeStart = dayjs.utc(nowDayJs.value).set('hour', +times[0].split(':')[0])
    .set('minute', +times[0].split(':')[1])
    .set('second', 0)
    .set('millisecond', 0)

  const timeEnd = dayjs.utc(nowDayJs.value).set('hour', +times[1].split(':')[0])
    .set('minute', +times[1].split(':')[1])
    .set('second', 0)
    .set('millisecond', 0)

  if (nowDayJs.value.isBefore(timeStart)) {
    isOpen.value = false
    diff = dayjs.utc(timeStart - nowDayJs.value)
    diffHH = diff.format('HH') === '00' ? '' : `${diff.format('HH')} ч.`
    return `Откроется через ${diffHH} ${diff.format('mm')} мин. `
  }

  if (nowDayJs.value.isBetween(timeStart, timeEnd)) {
    isOpen.value = true
    diff = dayjs.utc(timeEnd - nowDayJs.value)
    diffHH = diff.format('HH') === '00' ? '' : `${diff.format('HH')} ч.`
    return `Открыто еще ${diffHH} ${diff.format('mm')} мин. `
  }

  if (nowDayJs.value.isAfter(timeEnd)) {
    isOpen.value = false
    if (startNextWorkDay.value) {
      return willOpenIn.value;
    }
    return 'Закрыто'
  }

  return ''
})

const schedule = computed(() => {
  const data: { day: string, time: string, datetime: string }[] = []
  const {timetable} = terminal.value

  if (!timetable) {
    return data
  }

  daysOfWeeks.forEach(element => {
    const table = timetable[element.eng] || timetable.workday
    const fullDay = table.from === '00:01' && table.to === '23:59'
    const eng = element.eng[0].toUpperCase() + element.eng[1]
    const time = !table.isWorking
      ? '-'
      : fullDay ? '24 часа' : `${table.from} ${table.to}`
    data.push({
      day: element.short,
      time,
      datetime: table.isWorking
        ? fullDay ? eng : `${eng}, ${time.split(' ').join('-')}`
        : '',
    })
  })
  data.push(data.shift())
  return data
})

await until(() => terminal.value.guid).toBeTruthy()
</script>
